import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { TimeDisplay } from '../schedule/TimeDisplay';
import { useCaseHelpers } from '../../hooks/useCaseHelpers';
import { formatDateForDisplay } from '../../utils/dateUtils';
import type { Case } from '../../types';

interface SimplifiedCaseCardsProps {
  cases: Case[];
  selectedDate: string;
}

/**
 * 陸送員向けのシンプルな案件カード表示コンポーネント
 * 自分に割り当てられた案件に集中できるよう最適化
 */
export const SimplifiedCaseCards: React.FC<SimplifiedCaseCardsProps> = ({
  cases,
  selectedDate,
}) => {
  const navigate = useNavigate();
  const {
    safe: { getVehicleName, getBillingPartnerName, getPickupPartnerName, getDeliveryPartnerName },
  } = useCaseHelpers();

  // 案件がない場合の表示
  if (cases.length === 0) {
    return (
      <div className='text-center py-12'>
        <CalendarDaysIcon className='mx-auto h-16 w-16 text-gray-400 dark:text-gray-600 mb-4' />
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          {formatDateForDisplay(selectedDate)}の案件はありません
        </h3>
        <p className='text-gray-500 dark:text-gray-400'>今日は割り当てられた案件がありません</p>
      </div>
    );
  }

  return (
    <div className='mb-8'>
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
          <DocumentTextIcon className='h-5 w-5 mr-2' />
          本日の案件 ({cases.length}件)
        </h3>
        <button
          onClick={() => navigate('/cases')}
          className='inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
        >
          詳細を見る
          <ArrowTopRightOnSquareIcon className='ml-1 h-3 w-3' />
        </button>
      </div>

      <div className='grid gap-4'>
        {cases.map(case_ => (
          <div
            key={case_.id}
            onClick={() => navigate(`/cases/${case_.id}`)}
            className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
              !case_.assignedLandTransporterId // FL-142: 未割当判定
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <div className='flex items-start justify-between mb-3'>
              <div className='flex-1 min-w-0'>
                <h4 className='text-lg font-medium text-gray-900 dark:text-white truncate mb-1'>
                  {getBillingPartnerName(case_)}
                </h4>
                <p className='text-sm text-gray-500 dark:text-gray-400 mb-3'>
                  {getVehicleName(case_)}
                </p>

                {/* 時間情報 */}
                <div className='mb-3'>
                  <TimeDisplay
                    pickupTimeStart={case_.pickupTimeStart}
                    pickupTimeEnd={case_.pickupTimeEnd}
                    deliveryTime={case_.deliveryTime}
                    textSize='text-sm'
                    spacing='space-y-1'
                  />
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2'>
                    <div className='text-sm text-gray-500 dark:text-gray-400 truncate'>
                      取: {getPickupPartnerName(case_)}
                    </div>
                    <div className='text-sm text-gray-500 dark:text-gray-400 truncate'>
                      入: {getDeliveryPartnerName(case_)}
                    </div>
                  </div>
                </div>

                {/* ステータス表示 */}
                <div className='flex items-center'>
                  {!case_.assignedLandTransporterId && ( // FL-142: 未割当判定
                    <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'>
                      未割当
                    </span>
                  )}
                  {case_.status === 'assigned' && (
                    <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'>
                      割当済み
                    </span>
                  )}
                  {case_.status === 'in_transit' && (
                    <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'>
                      作業中
                    </span>
                  )}
                  {case_.status === 'completed' && (
                    <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'>
                      完了
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
